"use client";

import React, { useEffect, useMemo, useState, type CSSProperties } from "react";
import clsx from "clsx";
import { Link } from "@/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";
import type {
	FrontBannerV2Banner,
	FrontBannerV2ContentAnimation,
	FrontBannerV2ContentBlock,
	FrontBannerV2ContentPosition,
	FrontBannerV2PageConfig,
} from "@/lib/api/frontBanner";
import { isRenderableFrontBanner } from "@/lib/api/frontBanner";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";

const DEFAULT_DESKTOP_HEIGHT = 640;
const DEFAULT_TABLET_HEIGHT = 520;
const DEFAULT_MOBILE_HEIGHT = 420;
const DEFAULT_DESKTOP_HEIGHT_VH = 72;
const DEFAULT_TABLET_HEIGHT_VH = 60;
const DEFAULT_MOBILE_HEIGHT_VH = 58;
const DEFAULT_DESKTOP_MIN_HEIGHT = 520;
const DEFAULT_TABLET_MIN_HEIGHT = 420;
const DEFAULT_MOBILE_MIN_HEIGHT = 360;
const DEFAULT_CONTENT_WIDTH = 520;
const DEFAULT_CONTENT_WIDTH_UNIT = "px";
const DEFAULT_CONTENT_ANIMATION_DURATION = 600;
const DEFAULT_OVERLAY_OPACITY = 42;

const ANIMATION_STYLES = `
@keyframes frontBannerFadeIn {
	from { opacity: 0; }
	to { opacity: 1; }
}

@keyframes frontBannerSlideUp {
	from { opacity: 0; transform: translate3d(0, 22px, 0); }
	to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@keyframes frontBannerSlideLeft {
	from { opacity: 0; transform: translate3d(22px, 0, 0); }
	to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@keyframes frontBannerZoomIn {
	from { opacity: 0; transform: scale(0.92); }
	to { opacity: 1; transform: scale(1); }
}

@keyframes frontBannerMouseScroll {
	0% { transform: translateY(0); opacity: 1; }
	50% { transform: translateY(12px); opacity: 0.3; }
	100% { transform: translateY(0); opacity: 1; }
}

@keyframes frontBannerMouseFade {
	0% { color: rgba(255, 255, 255, 0.9); }
	50% { color: rgba(255, 255, 255, 0.2); }
	100% { color: rgba(255, 255, 255, 0.9); }
}

@keyframes frontBannerWheelMove {
	0% { transform: translateY(0); opacity: 1; }
	50% { transform: translateY(10px); opacity: 0.2; }
	100% { transform: translateY(0); opacity: 1; }
}

.front-banner-v2-swiper .swiper-button-prev,
.front-banner-v2-swiper .swiper-button-next {
	width: 38px;
	height: 38px;
	border-radius: 9999px;
	border: 1px solid rgba(255, 255, 255, 0.42);
	background: rgba(15, 23, 42, 0.18);
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
	color: #ffffff;
	opacity: 0.76;
	transition: background 180ms ease, border-color 180ms ease, opacity 180ms ease, transform 180ms ease;
}

.front-banner-v2-swiper .swiper-button-prev:hover,
.front-banner-v2-swiper .swiper-button-next:hover {
	border-color: rgba(255, 255, 255, 0.72);
	background: rgba(15, 23, 42, 0.32);
	opacity: 1;
	transform: scale(1.04);
}

.front-banner-v2-swiper .swiper-button-prev::after,
.front-banner-v2-swiper .swiper-button-next::after {
	font-size: 13px;
	font-weight: 600;
}

.front-banner-v2-swiper .swiper-pagination {
	bottom: 18px !important;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

.front-banner-v2-swiper .front-banner-v2-pagination-bullet {
	display: inline-block;
	width: 7px;
	height: 7px;
	box-sizing: border-box;
	border: 0 !important;
	outline: none !important;
	box-shadow: none !important;
	border-radius: 9999px;
	background: rgba(99, 179, 255, 0.78) !important;
	opacity: 1 !important;
	cursor: pointer;
	transition: width 280ms ease, background 280ms ease, opacity 280ms ease;
	-webkit-tap-highlight-color: transparent;
}

.front-banner-v2-swiper .front-banner-v2-pagination-bullet-active {
	display: inline-block;
	width: 28px;
	height: 7px;
	box-sizing: border-box;
	border-radius: 9999px;
	border: 0 !important;
	outline: none !important;
	box-shadow: none !important;
	background: #1677ff !important;
}

.front-banner-v2-swiper .front-banner-v2-pagination-bullet:focus,
.front-banner-v2-swiper .front-banner-v2-pagination-bullet:focus-visible,
.front-banner-v2-swiper .front-banner-v2-pagination-bullet-active:focus,
.front-banner-v2-swiper .front-banner-v2-pagination-bullet-active:focus-visible {
	border: 0 !important;
	outline: none !important;
	box-shadow: none !important;
}

.front-banner-v2-swiper .front-banner-v2-image {
	transform: scale(1);
	transition: transform 5200ms ease;
	will-change: transform;
}

.front-banner-v2-swiper .swiper-slide-active .front-banner-v2-image {
	transform: scale(1.045);
}

.front-banner-v2-scroll-indicator {
	animation: frontBannerMouseScroll 2s ease-in-out infinite;
}

.front-banner-v2-scroll-icon {
	animation: frontBannerMouseFade 2s ease-in-out infinite;
}

.front-banner-v2-scroll-wheel {
	animation: frontBannerWheelMove 2s ease-in-out infinite;
}

@media (max-width: 767px) {
	.front-banner-v2-swiper .swiper-button-prev,
	.front-banner-v2-swiper .swiper-button-next {
		width: 32px;
		height: 32px;
	}

	.front-banner-v2-swiper .swiper-button-prev::after,
	.front-banner-v2-swiper .swiper-button-next::after {
		font-size: 11px;
	}
}
`;

type BannerDevice = "desktop" | "tablet" | "mobile";

const getViewportDevice = (width: number): BannerDevice => {
	if (width < 768) {
		return "mobile";
	}

	if (width < 1024) {
		return "tablet";
	}

	return "desktop";
};

const getNumberValue = (value: unknown, fallback: number) => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
		return Number(value);
	}

	return fallback;
};

const clampBannerDimension = (value: unknown, fallback: number) => {
	const numeric = getNumberValue(value, fallback);
	return Math.max(120, Math.min(2400, numeric));
};

const clampBannerViewportHeight = (value: unknown, fallback: number) => {
	const numeric = getNumberValue(value, fallback);
	return Math.max(20, Math.min(100, numeric));
};

const clampContentWidth = (value: unknown, unit: FrontBannerV2Banner["content_width_unit"]) => {
	const fallback = unit === "percent" ? 48 : DEFAULT_CONTENT_WIDTH;
	const numeric = getNumberValue(value, fallback);

	if (unit === "percent") {
		return Math.max(20, Math.min(100, numeric));
	}

	return Math.max(240, Math.min(960, numeric));
};

const clampAnimationDuration = (value: unknown) => {
	const numeric = getNumberValue(value, DEFAULT_CONTENT_ANIMATION_DURATION);
	return Math.max(200, Math.min(2000, numeric));
};

const clampOpacity = (value: unknown, fallback = DEFAULT_OVERLAY_OPACITY) => {
	const numeric = getNumberValue(value, fallback);
	return Math.max(0, Math.min(100, numeric));
};

const hexToRgba = (hex = "#0f172a", opacity = DEFAULT_OVERLAY_OPACITY) => {
	const normalizedHex = hex.replace("#", "");
	const fullHex =
		normalizedHex.length === 3
			? normalizedHex
					.split("")
					.map((char) => `${char}${char}`)
					.join("")
			: normalizedHex.padEnd(6, "0").slice(0, 6);

	const red = Number.parseInt(fullHex.slice(0, 2), 16);
	const green = Number.parseInt(fullHex.slice(2, 4), 16);
	const blue = Number.parseInt(fullHex.slice(4, 6), 16);
	const alpha = clampOpacity(opacity) / 100;

	return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const getBannerHeightConfig = (pageConfig: FrontBannerV2PageConfig, device: BannerDevice) => {
	if (device === "mobile") {
		return {
			mode: pageConfig.mobile_height_mode || "fixed",
			fixedHeight: clampBannerDimension(pageConfig.mobile_height, DEFAULT_MOBILE_HEIGHT),
			viewportHeight: clampBannerViewportHeight(pageConfig.mobile_height_vh, DEFAULT_MOBILE_HEIGHT_VH),
			minHeight: clampBannerDimension(pageConfig.mobile_min_height, DEFAULT_MOBILE_MIN_HEIGHT),
		};
	}

	if (device === "tablet") {
		return {
			mode: pageConfig.tablet_height_mode || "fixed",
			fixedHeight: clampBannerDimension(pageConfig.tablet_height, DEFAULT_TABLET_HEIGHT),
			viewportHeight: clampBannerViewportHeight(pageConfig.tablet_height_vh, DEFAULT_TABLET_HEIGHT_VH),
			minHeight: clampBannerDimension(pageConfig.tablet_min_height, DEFAULT_TABLET_MIN_HEIGHT),
		};
	}

	return {
		mode: pageConfig.desktop_height_mode || "fixed",
		fixedHeight: clampBannerDimension(pageConfig.desktop_height, DEFAULT_DESKTOP_HEIGHT),
		viewportHeight: clampBannerViewportHeight(pageConfig.desktop_height_vh, DEFAULT_DESKTOP_HEIGHT_VH),
		minHeight: clampBannerDimension(pageConfig.desktop_min_height, DEFAULT_DESKTOP_MIN_HEIGHT),
	};
};

const getBannerHeightStyle = (pageConfig: FrontBannerV2PageConfig, device: BannerDevice): CSSProperties => {
	const heightConfig = getBannerHeightConfig(pageConfig, device);

	if (heightConfig.mode === "fullscreen") {
		return {
			height: device === "desktop" ? "var(--zoom-compensated-vh, 100vh)" : "100vh",
			minHeight: `${heightConfig.minHeight}px`,
		};
	}

	if (heightConfig.mode === "viewport") {
		return {
			height: `${heightConfig.viewportHeight}vh`,
			minHeight: `${heightConfig.minHeight}px`,
		};
	}

	return {
		height: `${heightConfig.fixedHeight}px`,
	};
};

const getContentWidthStyle = (
	width: FrontBannerV2Banner["content_width"],
	unit: FrontBannerV2Banner["content_width_unit"],
): CSSProperties => {
	const normalizedUnit = unit === "percent" ? "percent" : DEFAULT_CONTENT_WIDTH_UNIT;
	const safeWidth = clampContentWidth(width, normalizedUnit);

	return {
		width: "100%",
		maxWidth: normalizedUnit === "percent" ? `${safeWidth}%` : `${safeWidth}px`,
	};
};

const getContentAnimationStyle = (
	animation: FrontBannerV2ContentAnimation | undefined,
	duration: FrontBannerV2Banner["content_animation_duration"],
): CSSProperties => {
	const normalizedAnimation = animation || "none";
	if (normalizedAnimation === "none") {
		return {};
	}

	const animationNameMap: Record<Exclude<FrontBannerV2ContentAnimation, "none">, string> = {
		fade: "frontBannerFadeIn",
		"slide-up": "frontBannerSlideUp",
		"slide-left": "frontBannerSlideLeft",
		"zoom-in": "frontBannerZoomIn",
	};

	return {
		animation: `${animationNameMap[normalizedAnimation]} ${clampAnimationDuration(
			duration,
		)}ms cubic-bezier(0.22, 1, 0.36, 1)`,
	};
};

const getPositionClasses = (position: FrontBannerV2ContentPosition) => {
	switch (position) {
		case "top-left":
			return "items-start justify-start text-left";
		case "center-left":
			return "items-center justify-start text-left";
		case "center":
			return "items-center justify-center text-center";
		case "center-right":
			return "items-center justify-end text-right";
		case "bottom-left":
			return "items-end justify-start text-left";
		case "bottom-center":
			return "items-end justify-center text-center";
		case "bottom-right":
			return "items-end justify-end text-right";
		default:
			return "items-center justify-start text-left";
	}
};

const isExternalHref = (href: string) => /^https?:\/\//i.test(href);
const DEFAULT_FONT_SIZE_CLASS: Record<FrontBannerV2ContentBlock["type"], string> = {
	heading: "text-3xl sm:text-4xl lg:text-5xl",
	text: "text-sm sm:text-base lg:text-lg",
	li: "text-[11px] sm:text-xs",
	button: "text-sm sm:text-base",
};
const getFontSizeClass = (type: FrontBannerV2ContentBlock["type"], fontSize?: string) =>
	fontSize || DEFAULT_FONT_SIZE_CLASS[type];

export type BannerHeadingLevel = "h1" | "h2" | "div";

function MobileScrollIndicator() {
	return (
		<div className="pointer-events-none absolute bottom-12 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center max-md:flex">
			<div className="front-banner-v2-scroll-indicator">
				<svg
					width="24"
					height="40"
					viewBox="0 0 24 40"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className="front-banner-v2-scroll-icon"
					style={{ color: "rgba(255, 255, 255, 0.8)" }}
				>
					<rect
						x="2"
						y="2"
						width="20"
						height="32"
						rx="10"
						stroke="currentColor"
						strokeWidth="2"
						fill="none"
					/>
					<circle
						cx="12"
						cy="12"
						r="2"
						fill="currentColor"
						className="front-banner-v2-scroll-wheel"
					/>
				</svg>
			</div>
		</div>
	);
}

const renderContentBlock = (
	block: FrontBannerV2ContentBlock,
	defaultTextColor: string,
	textAlignClassName: string,
	headingLevel: BannerHeadingLevel,
) => {
	switch (block.type) {
		case "heading": {
			const HeadingTag = headingLevel;
			return (
				<HeadingTag
					key={block.id}
					className={clsx(
						"font-semibold leading-tight",
						getFontSizeClass("heading", block.font_size),
						textAlignClassName,
					)}
					style={{
						color: block.color || defaultTextColor,
					}}
				>
					{block.text}
				</HeadingTag>
			);
		}
		case "text":
			return (
				<div
					key={block.id}
					className={clsx(
						"leading-6 whitespace-pre-wrap",
						getFontSizeClass("text", block.font_size),
						textAlignClassName,
					)}
					style={{
						color: block.color || defaultTextColor,
						opacity: 0.92,
						overflowWrap: "anywhere",
						wordBreak: "break-word",
					}}
				>
					{block.text}
				</div>
			);
		case "li":
			return (
				<div key={block.id} className="flex w-full flex-wrap gap-2 pt-1">
					{block.items.map((item, index) => (
						<span
							key={`${block.id}-${index}-${item.text}`}
							className={clsx(
								"flex items-start gap-2 overflow-hidden border border-white/10 bg-black/30 px-3 py-1",
								getFontSizeClass("li", block.font_size),
								item.wrap ? "w-full max-w-full rounded-2xl" : "max-w-full rounded-full",
							)}
							style={{
								color: block.color || defaultTextColor,
								maxWidth: item.wrap ? "100%" : "min(100%, 320px)",
							}}
						>
							<span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#f6c343]" />
							<span
								className={clsx("block min-w-0", item.wrap ? "flex-1" : "max-w-full")}
								style={{
									whiteSpace: item.wrap ? "normal" : "nowrap",
									overflowWrap: item.wrap ? "anywhere" : "normal",
									wordBreak: item.wrap ? "break-word" : "normal",
									overflow: item.wrap ? "visible" : "hidden",
									textOverflow: item.wrap ? "clip" : "ellipsis",
								}}
							>
								{item.text}
							</span>
						</span>
					))}
				</div>
			);
		case "button":
			if (isExternalHref(block.href)) {
				return (
					<a
						key={block.id}
						href={block.href}
						target="_blank"
						rel="noreferrer"
						className={clsx(
							"inline-flex min-h-[42px] max-w-full items-center justify-center rounded-full px-5 py-2 font-medium transition-opacity hover:opacity-90",
							getFontSizeClass("button", block.font_size),
							block.full_width ? "w-full" : "w-fit min-w-[140px]",
							block.variant === "secondary" ? "border" : "",
						)}
						style={{
							backgroundColor:
								block.variant === "secondary" ? "transparent" : block.bg_color || "#ffd54a",
							borderColor:
								block.variant === "secondary"
									? block.bg_color || "rgba(255,255,255,0.35)"
									: "transparent",
							color:
								block.text_color ||
								(block.variant === "secondary" ? block.bg_color || "#ffffff" : "#0f172a"),
						}}
					>
						{block.text}
					</a>
				);
			}

			return (
				<Link
					key={block.id}
					href={block.href}
					className={clsx(
						"inline-flex min-h-[42px] max-w-full items-center justify-center rounded-full px-5 py-2 font-medium transition-opacity hover:opacity-90",
						getFontSizeClass("button", block.font_size),
						block.full_width ? "w-full" : "w-fit min-w-[140px]",
						block.variant === "secondary" ? "border" : "",
					)}
					style={{
						backgroundColor:
							block.variant === "secondary" ? "transparent" : block.bg_color || "#ffd54a",
						borderColor:
							block.variant === "secondary"
								? block.bg_color || "rgba(255,255,255,0.35)"
								: "transparent",
						color:
							block.text_color ||
							(block.variant === "secondary" ? block.bg_color || "#ffffff" : "#0f172a"),
					}}
				>
					{block.text}
				</Link>
			);
		default:
			return null;
	}
};

export default function FrontBannerRenderer({
	pageConfig,
	className,
	autoplayMs,
	headingLevel = "h1",
}: {
	pageConfig?: FrontBannerV2PageConfig | null;
	className?: string;
	autoplayMs?: number;
	headingLevel?: BannerHeadingLevel;
}) {
	const banners = useMemo(
		() =>
			(pageConfig?.banners || [])
				.filter(isRenderableFrontBanner)
				.sort((left, right) => left.sort - right.sort),
		[pageConfig],
	);
	const [viewportWidth, setViewportWidth] = useState(1440);

	useEffect(() => {
		const updateViewportWidth = () => setViewportWidth(window.innerWidth);
		updateViewportWidth();
		window.addEventListener("resize", updateViewportWidth);
		return () => window.removeEventListener("resize", updateViewportWidth);
	}, []);

	if (!pageConfig || banners.length === 0) {
		return null;
	}

	const device = getViewportDevice(viewportWidth);
	const swiperDelay = autoplayMs || pageConfig.autoplay_interval || 5000;
	const showMobileScrollIndicator = device === "mobile" && pageConfig.slug === "home";

	return (
		<div className={clsx("relative w-full overflow-hidden bg-black", className)}>
			<style jsx global>{ANIMATION_STYLES}</style>
			<Swiper
				className="front-banner-v2-swiper w-full"
				modules={[Autoplay, EffectFade, Navigation, Pagination]}
				slidesPerView={1}
				loop={banners.length > 1}
				autoHeight
				effect="fade"
				fadeEffect={{ crossFade: true }}
				speed={900}
				navigation={banners.length > 1}
				pagination={
					banners.length > 1
						? {
								clickable: true,
								bulletClass: "front-banner-v2-pagination-bullet",
								bulletActiveClass: "front-banner-v2-pagination-bullet-active",
								renderBullet: (index, className) =>
									`<span class="${className}" aria-label="Go to slide ${index + 1}"></span>`,
							}
						: false
				}
				autoplay={
					banners.length > 1
						? {
								delay: swiperDelay,
								disableOnInteraction: false,
							}
						: false
				}
			>
				{banners.map((banner) => {
					const slideImageUrl =
						device === "mobile" && banner.mobile_image?.url
							? banner.mobile_image.url
							: banner.desktop_image?.url || banner.mobile_image?.url || "";
					const textColor = banner.text_color || "#ffffff";
					const contentPositionClassName = getPositionClasses(banner.content_position);
					const textAlignClassName = contentPositionClassName.includes("text-center")
						? "text-center"
						: contentPositionClassName.includes("text-right")
							? "text-right"
							: "text-left";
					const bannerHeightStyle = getBannerHeightStyle(pageConfig, device);
					const hasContentBlocks = banner.content_blocks.length > 0;
					const contentOverlayColor =
						hasContentBlocks && banner.overlay_scope === "content"
							? hexToRgba(banner.overlay_color, banner.overlay_opacity)
							: "transparent";

					return (
						<SwiperSlide key={banner.id}>
							<div className="relative w-full overflow-hidden bg-black" style={bannerHeightStyle}>
								{slideImageUrl ? (
									<img
										src={slideImageUrl}
										alt={banner.desktop_image?.alt || banner.mobile_image?.alt || pageConfig.slug}
										className="front-banner-v2-image absolute inset-0 h-full w-full object-cover"
									/>
								) : (
									<div className="absolute inset-0 bg-[linear-gradient(135deg,#111827,#1f2937,#0f172a)]" />
								)}

								{banner.overlay_scope === "banner" ? (
									<div
										className="absolute inset-0"
										style={{
											backgroundColor: hexToRgba(banner.overlay_color, banner.overlay_opacity),
										}}
									/>
								) : null}

								{hasContentBlocks ? (
									<div
										className={clsx(
											"absolute inset-0 flex px-4 py-6 sm:px-8 sm:py-10 lg:px-16",
											contentPositionClassName,
										)}
									>
										<div
											key={`${banner.id}-${device}`}
											className="relative z-10 flex flex-col gap-3 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.35)] sm:p-6"
											style={{
												...getContentWidthStyle(banner.content_width, banner.content_width_unit),
												...getContentAnimationStyle(
													banner.content_animation,
													banner.content_animation_duration,
												),
												color: textColor,
												backgroundColor: contentOverlayColor,
											}}
										>
											{banner.content_blocks.map((block) =>
												renderContentBlock(block, textColor, textAlignClassName, headingLevel),
											)}
										</div>
									</div>
								) : null}
								{showMobileScrollIndicator ? <MobileScrollIndicator /> : null}
							</div>
						</SwiperSlide>
					);
				})}
			</Swiper>
		</div>
	);
}
